import { regions } from '../config/pricingConfig';

export const STORAGE_KEY = 'comet_estimates';

const RESOURCE_LABELS = {
  compute: 'Virtual Machines',
  manageddisk: 'Managed Disks',
  publicip: 'Public IP Addresses',
  storage: 'Storage Accounts',
  appservice: 'App Service Plans',
  sql: 'Azure SQL Databases',
  aks: 'AKS Clusters',
};

const REGION_LABELS = regions.reduce((lookup, region) => {
  lookup[region.value] = region.label;
  return lookup;
}, {});

const getSafeNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const getEstimateDate = (savedAt) => {
  const date = new Date(savedAt);
  return Number.isNaN(date.getTime()) ? null : date;
};

export const getItemMonthlyCost = (item = {}) => {
  const unitPrice = parseFloat(item.unitPrice);
  if (!Number.isFinite(unitPrice)) return 0;
  return unitPrice * getSafeNumber(item.quantity) * getSafeNumber(item.hours);
};

export const getEstimateTotal = (estimate = {}) => {
  const total = Number(estimate.total);
  if (Number.isFinite(total)) return total;
  return (estimate.items || []).reduce((sum, item) => sum + getItemMonthlyCost(item), 0);
};

export const loadSavedEstimates = () => {
  try {
    const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
    return Array.isArray(stored) ? stored : [];
  } catch {
    return [];
  }
};

const normalizeLabel = (value, labels) => {
  if (!value) return 'Unspecified';
  return labels?.[value] || value;
};

const buildTrend = (estimates) => {
  const datedEstimates = estimates
    .map((estimate) => ({
      date: getEstimateDate(estimate.savedAt),
      total: getEstimateTotal(estimate),
    }))
    .filter((estimate) => estimate.date);

  const latestDate = datedEstimates.reduce(
    (latest, estimate) => (estimate.date > latest ? estimate.date : latest),
    new Date()
  );

  const months = Array.from({ length: 6 }, (_, index) => {
    const date = new Date(latestDate.getFullYear(), latestDate.getMonth() - (5 - index), 1);
    return {
      key: `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`,
      label: date.toLocaleDateString('en-US', { month: 'short' }),
      total: 0,
      count: 0,
    };
  });

  const monthMap = new Map(months.map((month) => [month.key, month]));
  datedEstimates.forEach(({ date, total }) => {
    const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    const month = monthMap.get(key);
    if (month) {
      month.total += total;
      month.count += 1;
    }
  });

  return months;
};

const aggregateItems = (estimates, valueSelector, labels) => {
  const entries = new Map();

  estimates.forEach((estimate) => {
    (estimate.items || []).forEach((item) => {
      const key = valueSelector(item);
      const label = normalizeLabel(key, labels);
      const current = entries.get(label) || { label, total: 0, count: 0 };

      current.total += getItemMonthlyCost(item);
      current.count += 1;
      entries.set(label, current);
    });
  });

  return Array.from(entries.values()).sort((a, b) => b.total - a.total);
};

const buildInsights = ({
  topEstimate,
  topResource,
  topRegion,
  totalMonthlySpend,
  spendTrend,
}) => {
  const insights = [];

  if (topEstimate) {
    insights.push(`${topEstimate.name} is your largest saved estimate.`);
  }

  if (topResource && totalMonthlySpend > 0) {
    const resourceShare = Math.round((topResource.total / totalMonthlySpend) * 100);
    insights.push(`${topResource.label} account for about ${resourceShare}% of modeled monthly spend.`);
  }

  if (topRegion) {
    insights.push(`${topRegion.label} is your busiest region in saved estimates.`);
  }

  if (spendTrend.length >= 2) {
    const currentMonth = spendTrend[spendTrend.length - 1];
    const previousMonth = spendTrend[spendTrend.length - 2];
    const change = currentMonth.total - previousMonth.total;

    if (currentMonth.total > 0 || previousMonth.total > 0) {
      const direction = change >= 0 ? 'up' : 'down';
      insights.push(`Estimated spend is ${direction} ${Math.abs(change).toFixed(0)} month over month.`);
    }
  }

  return insights;
};

export const buildAnalyticsSnapshot = (estimates = []) => {
  const normalizedEstimates = [...estimates]
    .map((estimate) => ({
      ...estimate,
      items: Array.isArray(estimate.items) ? estimate.items : [],
      total: getEstimateTotal(estimate),
      savedDate: getEstimateDate(estimate.savedAt),
    }))
    .sort((a, b) => (b.savedDate?.getTime() || 0) - (a.savedDate?.getTime() || 0));

  const totalMonthlySpend = normalizedEstimates.reduce((sum, estimate) => sum + estimate.total, 0);
  const totalResources = normalizedEstimates.reduce((sum, estimate) => sum + estimate.items.length, 0);
  const uniqueRegions = new Set();

  normalizedEstimates.forEach((estimate) => {
    estimate.items.forEach((item) => {
      if (item.region) uniqueRegions.add(item.region);
    });
  });

  const resourceBreakdown = aggregateItems(normalizedEstimates, (item) => item.type, RESOURCE_LABELS);
  const regionBreakdown = aggregateItems(normalizedEstimates, (item) => item.region, REGION_LABELS);
  const spendTrend = buildTrend(normalizedEstimates);
  const topEstimate = normalizedEstimates[0]
    ? normalizedEstimates.reduce((top, estimate) => (estimate.total > top.total ? estimate : top))
    : null;

  return {
    totalMonthlySpend,
    estimateCount: normalizedEstimates.length,
    averageEstimateTotal: normalizedEstimates.length ? totalMonthlySpend / normalizedEstimates.length : 0,
    totalResources,
    regionCount: uniqueRegions.size,
    latestSavedAt: normalizedEstimates[0]?.savedAt || null,
    topEstimate,
    spendTrend,
    resourceBreakdown,
    regionBreakdown,
    recentEstimates: normalizedEstimates.slice(0, 5),
    insights: buildInsights({
      topEstimate,
      topResource: resourceBreakdown[0],
      topRegion: regionBreakdown[0],
      totalMonthlySpend,
      spendTrend,
    }),
  };
};
